import { Box, Button, CircularProgress, DialogActions, TextField, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { FC, useEffect, useState } from 'react'

import { CustomModal } from '../../common/CustomModal'

const RETURN_DATE_FORMAT = 'YYYY-MM-DD'

interface ReturnCheckModalProps {
  isOpen: boolean
  /** Pre-formatted summary lines describing the payment being returned. */
  summary: string
  /** The date the payment was received — the return cannot predate it. */
  paymentDate?: string | Date | null
  isLoading?: boolean
  onConfirm: (returnNotes: string, returnDate: string) => void
  onCancel: () => void
}

export const ReturnCheckModal: FC<ReturnCheckModalProps> = ({
  isOpen,
  summary,
  paymentDate,
  isLoading = false,
  onConfirm,
  onCancel
}) => {
  const [notes, setNotes] = useState('')
  const [returnDate, setReturnDate] = useState<Dayjs | null>(dayjs())

  // Reset per payment, not just on mount — the modal instance is reused for every row in the list,
  // so without this the previous check's date would carry over to the next one.
  useEffect(() => {
    if (isOpen) {
      setNotes('')
      setReturnDate(dayjs())
    }
  }, [isOpen])

  const minDate = paymentDate ? dayjs(paymentDate).startOf('day') : undefined
  const isDateValid = !!returnDate?.isValid() && (!minDate || !returnDate.startOf('day').isBefore(minDate))

  const handleCancel = () => {
    setNotes('')
    onCancel()
  }

  const handleConfirm = () => {
    if (!isDateValid) return
    onConfirm(notes.trim(), returnDate!.format(RETURN_DATE_FORMAT))
  }

  return (
    <CustomModal title="Mark Check as Returned" isOpen={isOpen} handleClose={handleCancel} maxWidth="md" width="600px">
      <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
          {summary}
        </Typography>

        <Box mb={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date the bank debited the returned check"
              value={returnDate}
              minDate={minDate}
              disabled={isLoading}
              inputFormat="MM/DD/YYYY"
              onChange={value => setReturnDate(value ? dayjs(value) : null)}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  required
                  error={!isDateValid}
                  helperText={
                    !isDateValid
                      ? 'Enter the date the bank took the money back — it cannot be before the deposit date.'
                      : 'Use the date shown on the bank statement, not today, so the reconciliation matches.'
                  }
                />
              )}
            />
          </LocalizationProvider>
        </Box>

        <TextField
          label="Reason for return (optional)"
          placeholder="e.g. Returned by bank — insufficient funds (NSF)"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          disabled={isLoading}
        />

        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
            {
              'This reverses the accounting effects of the payment (journal entries, allocations, trust/credit and account balances). The original deposit stays on the books at its full amount, and the money coming back out is recorded as a separate transaction on the date you enter above, so both sides appear on the bank reconciliation. This cannot be undone.'
            }
          </Typography>
        </Box>

        <DialogActions sx={{ padding: '20px 0 0', gap: 1 }}>
          <Button onClick={handleCancel} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={isLoading || !isDateValid}
            endIcon={isLoading && <CircularProgress size={20} color="inherit" />}
          >
            Mark as Returned
          </Button>
        </DialogActions>
      </Box>
    </CustomModal>
  )
}
